import { useState } from "react";
import { useForm } from "react-hook-form";
import useGroups from "admin/hooks/useGroups";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

interface GroupFormData {
  display_name: string;
  description: string;
}

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateGroupModal = ({ isOpen, onClose }: CreateGroupModalProps) => {
  const { showSuccess, showError } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<GroupFormData>({
    mode: "onChange",
    defaultValues: {
      display_name: "",
      description: "",
    },
  });

  const { mutate } = useGroups();

  const onSubmit = async (data: GroupFormData) => {
    trackEvent("admin_group_create_clicked");
    const url = `${VITE_ACCESS_CONTROLLER_API}/user-tags`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        showError("Failed to create group, please try again.");
      } else {
        showSuccess("Group created successfully");
        mutate();
        handleClose();
      }
    } catch (error) {
      showError("Failed to create group, please try again.");
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] bg-background flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 mb-4">
          <DialogTitle>New Group</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 space-y-4 mb-4">
            <div className="space-y-1">
              <Label htmlFor="name" className="pb-1 gap-0">
                Group Name<span className="text-destructive">*</span>
              </Label>
              <Input
                data-1p-ignore
                id="display_name"
                {...register("display_name", {
                  required: "Group name is required",
                })}
                placeholder="Enter group name"
                className={errors.display_name ? "border-destructive" : ""}
              />
              {errors.display_name && (
                <p className="text-sm text-destructive">{errors.display_name.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="description" className="pb-1 gap-0">
                Description<span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                {...register("description", {
                  required: "Description is required",
                })}
                placeholder="Describe the purpose and responsibilities of this group..."
                rows={4}
                className={errors.description ? "border-destructive" : ""}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-4 px-6 py-4 border-t border-border">
            <Button type="button" variant="outline" onClick={handleClose} size="lg">
              Cancel
            </Button>
            <Button
              type="submit"
              size="lg"
              disabled={!isValid || isSubmitting}
              className="min-w-[96px]"
            >
              {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGroupModal;
